// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { OrderChangeRequestModel } from "@store/Framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import constants from "@store/constants";
import setChangeRequestOrder from "@store/Framework/Store/Greif/Pages/OrderChange/Handler/SetChangeRequestOrder";
import React, { FC, useContext, useEffect, useState } from "react";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { PageErrorWrapper, PageErrorWrapperStyles } from "@insite/content-library/Components/PageErrorWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import OrderLayout from "@store/Library/Components/Toolkit/Layout/OrderLayout";
import NavigationBlocker from "@store/Library/Components/NavigationBlocker";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import ConfirmationMessage, {
    ConfirmationMessagePresentationProps,
} from "@store/Library/Components/ConfirmationMessage";
import translate from "@insite/client-framework/Translate";
import useTrackLink from "@store/Framework/GoogleAnalytics/hooks/useTrackLink";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

export interface OrderChangeRequestReviewPageContainerProps extends WidgetProps {}

export interface OrderChangeRequestReviewPageContainerStyles {
    pageErrorWrapper?: PageErrorWrapperStyles;
    confirmationMessage?: ConfirmationMessagePresentationProps;
}

export const orderChangeRequestReviewPageContainerStyles: OrderChangeRequestReviewPageContainerStyles = {
    confirmationMessage: {
        extendedStyles: {
            wrapper: {
                css: css`
                    max-width: ${pxToRem(700)};
                    margin: 0 auto;
                `,
            },
        },
    },
};

const styles = orderChangeRequestReviewPageContainerStyles;

const OrderChangeRequestReviewPageContainer: FC<OrderChangeRequestReviewPageContainerProps> = ({ id }) => {
    const dispatch = useAppDispatch();
    const orderDataView = useContext(OrderStateContext);
    const { value: order } = orderDataView;
    const { changeRequestOrder, requestSubmitted, failedToSubmitRequest } = useAppSelector(
        state => state.greif.pages.orderChange,
    );
    const orderHistoryLink = usePageLinkByPageType(constants.pageType.orderHistory);
    const orderChangeRequestLink = usePageLinkByPageType(constants.pageType.orderChangeRequest);
    const toasterContext = useContext(ToasterContext);
    const trackLink = useTrackLink();

    const [isInvalidData, setIsInvalidData] = useState(false);
    const orderChangeRequestId = order?.id ? `orderChange-${order.id}` : undefined;
    const isReady = orderChangeRequestId && orderChangeRequestId === changeRequestOrder.orderId;

    useEffect(() => {
        if (requestSubmitted) {
            trackLink({ page_type: "Review Order Change Request", link_clicked: "Send Change Request - Sent" });
        }
    }, [requestSubmitted]);

    useEffect(() => {
        if (failedToSubmitRequest) {
            toasterContext.addToast({
                body: translate("An error occurred. Try again later."),
                messageType: "warning",
            });

            trackLink({ page_type: "Review Order Change Request", link_clicked: "Send Change Request - Failed" });
        }
    }, [failedToSubmitRequest]);

    useEffect(() => {
        if (!orderChangeRequestId) {
            return;
        }

        if (isReady) {
            setIsInvalidData(false);
            return;
        }

        const orderChangeRequestJson = sessionStorage.getItem(orderChangeRequestId);

        if (!orderChangeRequestJson) {
            setIsInvalidData(true);
            return;
        }

        try {
            const cachedOrderChangeRequest: OrderChangeRequestModel = JSON.parse(orderChangeRequestJson);
            dispatch(setChangeRequestOrder({ changeRequestOrder: cachedOrderChangeRequest }));
            setIsInvalidData(false);
        } catch {
            sessionStorage.removeItem(orderChangeRequestId);
            setIsInvalidData(true);
        }
    }, [isReady, orderChangeRequestId]);

    return (
        <PageErrorWrapper
            value={orderDataView}
            badRequestMessage={siteMessage("Order_BadRequest")}
            notAuthorizedMessage={siteMessage("Order_NotAuthorized")}
            notFoundMessage={siteMessage("Order_NotFound")}
            extendedStyles={styles.pageErrorWrapper}
            customErrorMessage={
                isInvalidData ? translate("There is no change request to review for this order.") : undefined
            }
        >
            {requestSubmitted ? (
                <StyledWrapper data-test-selector="orderChangeRequestConfirmation">
                    <ConfirmationMessage
                        {...styles.confirmationMessage}
                        body={siteMessage("OrderChangeRequest_ConfirmationMessage")}
                        buttonHref={orderHistoryLink?.url}
                        buttonText={translate("Go back to Order History")}
                        headline={translate("Order change request has been sent")}
                    />
                </StyledWrapper>
            ) : (
                <>
                    <OrderLayout id={id} loading={!isReady} isV2 />
                    {/* Exit Confirmation Prompt — block leaving the change flow, but allow back-to-edit (step 1). */}
                    <NavigationBlocker
                        isActive={!!isReady}
                        allowedPaths={[orderChangeRequestLink?.url]}
                        title={siteMessage("OrderChangeRequest_ExitConfirmationHeadline")}
                        text={siteMessage("OrderChangeRequest_ExitConfirmationMessage")}
                        leaveLabel={translate("Cancel Change")}
                    />
                </>
            )}
        </PageErrorWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderChangeRequestReviewPageContainer,
    definition: {
        allowedContexts: [constants.pageType.orderChangeRequestReview],
        displayName: "Order Change Request Review Page Container",
        group: constants.widgetGroup.orderChangeRequestReview,
    },
};

export default widgetModule;
